/**
 * /prfaq 的中文变体。SEO 目标词:prfaq、pr faq、amazon prfaq、
 * working backwards document、press release faq。
 *
 * 策略与英文版一致:先把亚马逊「Working Backwards / 逆向工作法」的
 * PR/FAQ 方法讲清楚(新闻稿写什么、内外部 FAQ 各回答什么),给搜索者
 * 真价值;再诚实承认方法本身很好,问题在 substrate——PR/FAQ 是一份
 * 静态文档,定稿后产品一演进,里面的决策(定价、范围、目标客户)就和
 * 现实脱节,且没人会回头读。Draftlize 让这些决策成为结构化卡片,AI
 * agent 能读也能写,上游一变下游自动标 stale,且能被下游 PRD/spec 引用。
 *
 * 仅中文 + 内联(无 i18n),与 DecisionLogZh.tsx 及对比页保持一致。
 */
import { MarketingShell } from './MarketingShell';

export function PrfaqZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/prfaq"
      documentTitle="PR/FAQ 模板:亚马逊逆向工作法文档 · Draftlize"
      documentDescription="什么是 PR/FAQ,怎么写——亚马逊 Working Backwards(逆向工作法)的核心文档:动工前先写一篇假想新闻稿,加一组内部和外部 FAQ。方法很好,问题在于它是一份静态文档。Draftlize 让 PR/FAQ 里的决策——定价、范围、目标客户——不随产品演进而漂移。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 逆向工作法</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            亚马逊式 PR/FAQ——<br/>
            <em>但不会写完就过时。</em>
          </h1>
          <p className="lede">
            <em>PR/FAQ</em> 是亚马逊 <em>Working Backwards(逆向工作法)</em>的核心:在写第一行代码之前,先写一篇假想的新闻稿,宣布产品已经做成,再配一组 FAQ 反复拷问它。它逼团队从客户和结果出发,而不是从待办列表出发。方法本身是真的好——这不是问题所在。问题在于这份产物。PR/FAQ 是一份静态文档,定稿那天,就是它开始和你真正交付的产品脱节那天。这篇文章先教你把它写对,再讲清 Draftlize 如何让里面的<em>决策</em>——定价、范围、卖给谁——不悄悄变成空话。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">这套方法</span>
            <h2 className="serif">逆向工作法,<em>浓缩成一页。</em></h2>
          </div>
          <p>PR/FAQ 分两半:一页假想新闻稿,描绘上线那天;一组 FAQ,把它逐条压力测试。在动工前写。如果你既写不出一篇打动人的新闻稿,又扛不住 FAQ 的拷问,说明这个想法还没成熟——你刚好省下一个季度。下面是每个部分到底装什么。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '新闻稿',
              hint: '一页',
              body: '一篇简短、像真的一样的产品上线公告,日期写在上线那天。标题、副标题、用客户原话描述的痛点、你的解法、一句来自负责人的引言、一句来自满意客户的引言,以及怎么开始用。大白话,不堆术语——如果它打动不了一个陌生人,就打动不了市场。',
            },
            {
              k: '标题与痛点',
              hint: '客户优先',
              body: '先点客户和痛点,再讲产品。逆向工作法意味着这篇稿子读起来是客户拿到的结果,而不是你做了哪些功能。痛点不锋利,后面一切都白搭。',
            },
            {
              k: '外部 FAQ',
              hint: '面向客户',
              body: '客户或记者会问的问题:这是什么、卖多少钱、和别家有什么不同、什么时候能用、我的数据安全吗。这些答案就是对外的决策——定价、定位、可用性——写到能直接公开发布的程度。',
            },
            {
              k: '内部 FAQ',
              hint: '面向团队',
              body: '管理层会问的硬问题:为什么是我们、为什么是现在、这块蛋糕多大、可能出什么岔子、我们必须相信什么、我们坚决不做什么。范围、目标客户、那些有风险的假设,都在这里被钉死——它们是此后每一份 spec 的源头决策。',
            },
            {
              k: '假设与风险',
              hint: '什么必须成立',
              body: '把整个计划赖以成立的信念明确写出来。「我们假设客户愿意按用量付费,而不是按席位。」把它们摆上台面正是这个练习的意义——每一条都是一个决策,你要么之后确认它,要么被迫回头重议。',
            },
            {
              k: '我们坚决不做什么',
              hint: '范围',
              body: '刻意定下的非目标。亚马逊坚持要写这一条,因为范围是一个决策,而一个没写明的非目标,三个迭代后就会变成范围蔓延。这是一个团队是否真的「决定了」最清楚的信号。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          方法讲完了。下面:为什么这份文档一定稿就开始漂移——以及我们怎么解决。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么 PR/FAQ 会<em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它是一张快照,定格在定稿那一刻</h3>
            <p>PR/FAQ 记录的是签字那天你相信的东西。但产品一直在动——定价调整、范围收窄、目标客户变清晰。文档不会跟着动。到上线时,新闻稿描述的是一个已经不存在的产品,FAQ 用上个季度的决策回答着今天的问题。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">里面的决策和下游没有任何链接</h3>
            <p>PR/FAQ 正是你拍定价、定范围、定「坚决不做什么」的地方。然后 PRD、各份 spec、工程工单全都默认了这些决策。可你之后改了定价决策,没有任何东西把它链回 PR/FAQ,也没链向那些依赖它的 spec——于是它们悄无声息地错了。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">没人会回头读文档,但 agent 会</h3>
            <p>PR/FAQ 只在评审会上干了一次活,然后就归档了。它当初暴露的那些假设,本该随着你的认知更新被反复回看。可一份静态文档不是读者,它没法察觉自己记下的某个假设早已被证伪。于是漂移在无人盯防中越积越多。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份 PR/FAQ,<em>但是活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个决策成为一张卡片,而非一段文字</h3>
            <p>在 Draftlize 里写新闻稿和 FAQ,里面那些承重的决策——定价、范围、目标客户、各条非目标、每个有风险的假设——都成为有类型的卡片:结构化、有 ID 可寻址、能被 PRD 引用,而不是埋在文档里。叙事仍是叙事,但<em>决策</em>有了脊梁。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策,依赖它的卡片自动标 stale</h3>
            <p>改了定价决策,所有依赖它的卡片——那条外部 FAQ 答案、那段 PRD、那份默认按用量计费的 spec——都会自动变 stale,就像构建系统会让一个被改文件的所有下游失效一样。PR/FAQ 不再是一张快照,而是开始跟踪现实。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能把整份文档读也能写</h3>
            <p>Claude Code 或 Cursor 通过 MCP,在起草 PRD 之前先读完新闻稿和内外部 FAQ,再把新决策按同样的结构写回去。你在前期做的逆向工作法思考,变成 agent 每一轮都遵守的活上下文——而不是一份读者永远不会再打开的文档。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          逆向工作法是正确的思考方式。错在于:刚思考完,就把它冻进一份文档里。
        </blockquote>
        <cite>方法照旧。让 substrate 替你把决策保持最新。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">免费写你的 PR/FAQ,<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账户送 $5</span>
          <span>用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            按亚马逊的方式写出新闻稿和 FAQ,让 agent 把里面的决策变成卡片,再帮你把每一个决策——以及依赖它们的 PRD——随着认知更新保持最新。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
